/**
 * Client identity — the two knobs tokens.css was built around.
 *
 * `--gov-primary` and `--gov-gold` rebrand the app: every accent, focus ring and
 * selected state derives from the first, and the rail's "you are here" mark from
 * the second. This module turns that into a setting: it writes the pair onto the
 * document root, where it overrides the token defaults for every component at
 * once. No stylesheet knows this feature exists.
 *
 * Persistence belongs to the tenant Settings table. This module holds only the
 * current in-memory pair so previews repaint instantly without turning one
 * browser's localStorage into an accidental tenant configuration source.
 *
 * THE RAIL IS PAINTED IN THE MAIN COLOUR (restored 2026-10-19 — a 2026-10 pass
 * had turned it into a dark ink of the identity, so any colour a client applied
 * showed as near-black). And a GUARD: whatever hex a client picks, the accent it
 * writes carries white text, and the accent used as text reads on the surface,
 * in both themes.
 */

import { contrastRatio, hexToHsl, hslToHex, mixHex, normalizeHex } from '../components/colorUtils'
import { getTheme, subscribe } from '../lib/theme'

/** The platform identity, from tokens.css. An empty value means "use these". */
export const DEFAULT_MAIN = '#0e7a63'
export const DEFAULT_ACTIVE = '#c9a24b'

/**
 * The rail is painted IN the main colour.
 *
 * `base` is the client's own hex, unchanged, whenever it can be. The offsets are
 * lightness deltas around it, so the gradient is the main colour lit slightly at
 * the top and settled slightly at the foot. `well` and `rim` are the same ladder
 * used for the rail's STATES — the open section's recess, its rim, the wash behind
 * the page you are on — steps in lightness rather than an alpha of the accent,
 * because how far an accent lifts a surface depends on how light it is.
 */
const RAIL_OFFSET = { top: 2.5, base: 0, bottom: -5.5, well: -4.5, rim: 3.5 }

/**
 * The floor the rail's LIGHTEST stop has to clear against white — the one thing
 * allowed to move a client's colour, and only downward: a brand too light to carry
 * the rail's white text is deepened, hue and saturation untouched, by the smallest
 * step that gets there. 5.5 rather than AA's 4.5, for the secondary ink at reduced
 * alpha.
 */
const RAIL_MIN_CONTRAST = 5.5

/** A washed-out brand would give a grey rail; a neon one, a vibrating rail. */
const RAIL_SATURATION = { min: 32, max: 78 }

/**
 * The rail in the dark theme: the same hue at a calmer saturation, its lightness
 * capped just above the app field, so the column still reads as the identity
 * without becoming the brightest thing on a dark screen.
 */
const RAIL_DARK = { maxLightness: 11.5, saturation: { min: 26, max: 58 } }

/** The card surface, per theme — tokens.css `--bg-surface`. */
const SURFACE = { light: '#ffffff', dark: '#16191e' }

/** AA for normal text. */
const TEXT_FLOOR = 4.5

let currentBrand = { main: '', active: '' }

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

/**
 * Walks a colour's lightness — hue and saturation untouched — until `test`
 * passes, half a point at a time, so a colour that needs adjusting loses as
 * little of itself as possible and one that does not is never touched.
 */
function walk(hex, direction, test) {
  const hsl = hexToHsl(hex)
  if (!hsl) return hex
  let l = hsl.l
  let out = normalizeHex(hex)
  for (let step = 0; step < 200 && !test(out); step += 1) {
    l = clamp(l + direction * 0.5, 0, 100)
    out = hslToHex({ h: hsl.h, s: hsl.s, l })
    if (l === 0 || l === 100) break
  }
  return out
}

/**
 * The accent FILL: the client's colour, deepened only if white text on it would
 * fall under AA — a pale or neon brand still gets a primary button you can read.
 */
export function guardAccent(main) {
  if (!hexToHsl(main)) return ''
  return walk(main, -1, (hex) => contrastRatio('#ffffff', hex) >= TEXT_FLOOR)
}

/**
 * The accent as INK — links, the active tab, a selected label — on the card
 * surface of the theme on screen: darkened on white, lightened on charcoal,
 * until it clears AA.
 */
export function accentInk(main, theme = getTheme()) {
  const accent = guardAccent(main)
  if (!accent) return ''
  const dark = theme === 'dark'
  const ground = SURFACE[dark ? 'dark' : 'light']
  /* Start from the same mix tokens.css uses, so an identity that already passes
     renders exactly as the stylesheet default would. */
  const start = dark ? mixHex(accent, '#ffffff', 0.34) : mixHex(accent, '#03231b', 0.22)
  return walk(start, dark ? 1 : -1, (hex) => contrastRatio(hex, ground) >= TEXT_FLOOR)
}

/**
 * The stops the rail is built from, or null when `main` is not a colour — in which
 * case the tokens.css defaults (this same ladder, run on the platform emerald)
 * stay in force.
 */
export function railStops(main, theme = getTheme()) {
  const hsl = hexToHsl(main)
  if (!hsl) return null
  const dark = theme === 'dark'
  const band = dark ? RAIL_DARK.saturation : RAIL_SATURATION
  const s = clamp(hsl.s, band.min, band.max)
  const at = (l) => hslToHex({ h: hsl.h, s, l: clamp(l, 0, 100) })

  if (dark) {
    /* No contrast walk: the ceiling already puts every stop far below white. */
    const l = Math.min(hsl.l, RAIL_DARK.maxLightness)
    return {
      top: at(l + RAIL_OFFSET.top),
      base: at(l),
      bottom: at(l + RAIL_OFFSET.bottom / 2),
      well: at(l + RAIL_OFFSET.well / 2),
      rim: at(l + RAIL_OFFSET.rim),
    }
  }

  /* Step the whole ladder down together until its lightest stop carries white. */
  let l = hsl.l
  while (l > 6 && contrastRatio(at(l + RAIL_OFFSET.top), '#ffffff') < RAIL_MIN_CONTRAST) {
    l -= 0.5
  }

  return {
    top: at(l + RAIL_OFFSET.top),
    base: at(l),
    bottom: at(l + RAIL_OFFSET.bottom),
    well: at(l + RAIL_OFFSET.well),
    rim: at(l + RAIL_OFFSET.rim),
  }
}

/** The rail's representative colour — what a contrast check measures against. */
export const railColor = (main, theme = getTheme()) => railStops(main || DEFAULT_MAIN, theme)?.base ?? ''

/**
 * The active colour as the rail paints it — Sidebar.css lightens it 24% toward
 * white so it reads on the rail. A contrast check has to measure what is drawn.
 */
export const railMark = (active) => mixHex(active, '#ffffff', 0.24)

/** The variables a brand pair owns. */
function variables({ main, active }) {
  const accent = guardAccent(main)
  const rail = railStops(main)
  return {
    '--gov-primary': accent,
    '--sidebar-accent': accent,
    '--accent-ink': accentInk(main),
    '--sidebar-bg': rail?.base,
    '--sidebar-bg-top': rail?.top,
    '--sidebar-bg-bottom': rail?.bottom,
    '--sidebar-well': rail?.well,
    '--sidebar-rim': rail?.rim,
    '--gov-gold': active,
  }
}

/** Writes the pair onto :root. A falsy value REMOVES the override. */
export function applyBranding(brand) {
  const root = document.documentElement
  for (const [name, value] of Object.entries(variables(brand ?? {}))) {
    if (value) root.style.setProperty(name, value)
    else root.style.removeProperty(name)
  }
}

export function readBranding() {
  return { ...currentBrand }
}

export function saveBranding(brand) {
  currentBrand = {
    main: normalizeHex(brand?.main),
    active: normalizeHex(brand?.active),
  }
  applyBranding(currentBrand)
}

/** Converts the login/session settings dictionary into the UI identity pair. */
export function brandFromSettings(settings) {
  return {
    main: normalizeHex(settings?.BrandPrimaryColor),
    active: normalizeHex(settings?.BrandActiveColor),
  }
}

/** Called before first paint; the server pair is applied as soon as login/session loads. */
export function bootBranding() {
  applyBranding(currentBrand)
  /* The accent ink and the rail depend on the theme and are written as INLINE
     styles on :root — which beat the stylesheet's dark block. So a stored brand is
     run again whenever the theme changes, or the light rail would survive into
     dark. With no brand stored nothing is written and tokens.css holds both
     versions on its own. */
  subscribe(() => applyBranding(currentBrand))
}
